import { getIncidents } from './incidentsService';

export class IncidentPredictor {
  constructor() {
    this.model = null;
    this.isTrained = false;
  }

  async trainModel(incidents) {
    // Симуляція тренування ML моделі
    console.log('Training AI model with', incidents.length, 'incidents');
    
    // Аналіз патернів для прогнозування
    const patterns = this.analyzePatterns(incidents);
    const seasonality = this.analyzeSeasonality(incidents);
    const correlations = this.findCorrelations(incidents);
    
    this.model = {
      patterns,
      seasonality,
      correlations,
      trainedAt: new Date()
    };
    
    this.isTrained = true;
    
    return {
      accuracy: this.calculateAccuracy(incidents),
      features: Object.keys(patterns).length,
      trainingSize: incidents.length
    };
  }

  analyzePatterns(incidents) {
    const patterns = {
      timeBased: {},
      typeBased: {},
      clientBased: {},
      priorityTrends: {}
    };

    // Аналіз за часами доби
    incidents.forEach(incident => {
      const hour = new Date(incident.createdAt).getHours();
      patterns.timeBased[hour] = (patterns.timeBased[hour] || 0) + 1;
    });

    // Аналіз за типами
    incidents.forEach(incident => {
      patterns.typeBased[incident.type] = (patterns.typeBased[incident.type] || 0) + 1;
    });

    // Аналіз за клієнтами
    incidents.forEach(incident => {
      const client = incident.clientName || incident.clientId;
      if (client) {
        patterns.clientBased[client] = (patterns.clientBased[client] || 0) + 1;
      }
    });

    // Тренди пріоритетів
    incidents.forEach(incident => {
      patterns.priorityTrends[incident.priority] = (patterns.priorityTrends[incident.priority] || 0) + 1;
    });

    return patterns;
  }

  analyzeSeasonality(incidents) {
    const seasonality = {
      daily: {},
      weekly: {},
      monthly: {}
    };

    incidents.forEach(incident => {
      const date = new Date(incident.createdAt);
      
      // Денна сезонність
      const day = date.toDateString();
      seasonality.daily[day] = (seasonality.daily[day] || 0) + 1;
      
      // Тижнева сезонність
      const week = this.getWeekNumber(date);
      seasonality.weekly[week] = (seasonality.weekly[week] || 0) + 1;
      
      // Місячна сезонність
      const month = date.getMonth();
      seasonality.monthly[month] = (seasonality.monthly[month] || 0) + 1;
    });

    return seasonality;
  }

  findCorrelations(incidents) {
    const correlations = {
      typePriority: {},
      clientType: {},
      timeType: {}
    };

    incidents.forEach(incident => {
      // Кореляція тип-пріоритет
      const typePriorityKey = `${incident.type}_${incident.priority}`;
      correlations.typePriority[typePriorityKey] = (correlations.typePriority[typePriorityKey] || 0) + 1;

      // Кореляція клієнт-тип
      const client = incident.clientName || incident.clientId;
      if (client) {
        const clientTypeKey = `${client}_${incident.type}`;
        correlations.clientType[clientTypeKey] = (correlations.clientType[clientTypeKey] || 0) + 1;
      }

      // Кореляція час-тип
      const hour = new Date(incident.createdAt).getHours();
      const timeTypeKey = `${hour}_${incident.type}`;
      correlations.timeType[timeTypeKey] = (correlations.timeType[timeTypeKey] || 0) + 1;
    });

    return correlations;
  }

  predictNextIncidents(days = 7) {
    if (!this.isTrained) {
      throw new Error('Model not trained');
    }

    const predictions = [];
    const now = new Date();

    for (let i = 0; i < days; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() + i);

      const prediction = {
        date: date.toISOString().split('T')[0],
        expectedCount: this.predictDailyCount(date),
        highRiskTypes: this.predictHighRiskTypes(date),
        recommendedActions: this.generateRecommendations(date)
      };

      predictions.push(prediction);
    }

    return predictions;
  }

  predictDailyCount(date) {
    const dayOfWeek = date.getDay();
    const month = date.getMonth();
    const baseCount = 2; // Базовий рівень

    // Проста евристика на основі історичних даних
    let multiplier = 1.0;
    
    // Понеділок - більше інцидентів
    if (dayOfWeek === 1) multiplier *= 1.3;
    
    // Вихідні - менше інцидентів
    if (dayOfWeek === 0 || dayOfWeek === 6) multiplier *= 0.7;

    // Сезонність (припущення)
    if (month === 6 || month === 7) multiplier *= 1.2; // Літо
    if (month === 11 || month === 0) multiplier *= 1.1; // Зима

    return Math.round(baseCount * multiplier);
  }

  predictHighRiskTypes(date) {
    const hour = date.getHours();
    const highRiskTypes = [];

    // Евристика на основі кореляцій
    if (hour >= 9 && hour <= 11) {
      highRiskTypes.push({ type: 'client', confidence: 0.8 });
    }
    
    if (hour >= 14 && hour <= 16) {
      highRiskTypes.push({ type: 'technical', confidence: 0.7 });
    }

    return highRiskTypes;
  }

  generateRecommendations(date) {
    const recommendations = [];
    const dayOfWeek = date.getDay();
    const hour = date.getHours();

    // Рекомендації на основі патернів
    if (dayOfWeek === 1 && hour === 8) { // Понеділок ранок
      recommendations.push({
        type: 'preventive',
        message: 'Перевірте обладнання після вихідних',
        priority: 'medium'
      });
    }

    if (hour === 10) { // Пік активності
      recommendations.push({
        type: 'resource',
        message: 'Забезпечте додаткову підтримку операторів',
        priority: 'low'
      });
    }

    return recommendations;
  }

  calculateAccuracy(incidents) {
    // Спрощена метрика точності
    const recentIncidents = incidents.slice(0, 30);
    if (recentIncidents.length < 10) return 0.7;

    let correctPredictions = 0;
    
    // Базова перевірка патернів
    recentIncidents.forEach(incident => {
      const hour = new Date(incident.createdAt).getHours();
      const expectedTypes = this.predictHighRiskTypes(new Date(incident.createdAt));
      
      if (expectedTypes.some(t => t.type === incident.type)) {
        correctPredictions++;
      }
    });

    return Math.min(0.95, correctPredictions / recentIncidents.length);
  }

  getWeekNumber(date) {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }
}

export const incidentPredictor = new IncidentPredictor();